import {Card} from 'platform/components';
import {Hide, Show} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {CustomerResponseBodyV2} from '@dms/api/customer';
import {
  RentalOrderResponseBody,
  useChangeRentalOrderCustomerMutation,
  useCreateRentalOrderMutation,
  useReserveRentalOrderVehicleMutation,
} from '@dms/api/rental';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {CustomerMatchOrCreate, CustomerWidgetCard, handleApiError} from '@dms/shared';

import {suffixTestId, TestIdProps, useBoolean} from 'shared/web';

interface CustomerCardProps extends TestIdProps {
  rentalOrder: RentalOrderResponseBody | undefined;
  setRentalOrderId: (rentalOrderId: string | undefined) => void;
}

export function CustomerCard(props: CustomerCardProps) {
  const [isChangeModeOpen, openChangeMode, closeChangeMode] = useBoolean(false);
  const isFinishingRentalOrder = isNotNil(props.rentalOrder?.rentalVehicleId);

  const [updateRentalOrderCustomer, {isLoading: isUpdatingRentalOrderCustomer}] =
    useChangeRentalOrderCustomerMutation();
  const [createRentalOrder, {isLoading: isCreatingRentalOrder}] = useCreateRentalOrderMutation();
  const [reserveRentalVehicle, {isLoading: isReservingRentalVehicle}] =
    useReserveRentalOrderVehicleMutation();

  const handleUpdateCustomer = (customer: CustomerResponseBodyV2) => {
    if (!props.rentalOrder) {
      createRentalOrder({
        createRentalOrderRequestBody: {
          customerId: customer.id,
          rentalPeriod: null,
          rentalVehicleId: null,
        },
      })
        .unwrap()
        .then((data) => props.setRentalOrderId(data.id))
        .catch(handleApiError);

      return;
    }

    updateRentalOrderCustomer({
      changeRentalOrderCustomerRequestBody: {
        customerId: customer.id,
      },
      rentalOrderId: props.rentalOrder.rentalOrderId,
    })
      .unwrap()
      .then(async () => {
        if (isFinishingRentalOrder && props.rentalOrder?.state === 'draft') {
          await reserveRentalVehicle({
            rentalOrderId: props.rentalOrder!.rentalOrderId,
          })
            .unwrap()
            .catch(handleApiError);
        }
      })
      .then(closeChangeMode)
      .catch(handleApiError);
  };

  const isLoading =
    isUpdatingRentalOrderCustomer || isCreatingRentalOrder || isReservingRentalVehicle;

  return (
    <>
      <Hide when={isChangeModeOpen}>
        <CustomerWidgetCard
          data-testid={suffixTestId('customerWidgetCard', props)}
          onCustomer={handleUpdateCustomer}
          // right now, service case display output is 1:1 matching with rental order output,
          // this might change later.
          secondStepComponentType="SERVICE_CASE"
          isDetailLinkVisible
          hasVerification
          canChangeCustomer
          isLoading={isLoading}
          isLoadingOnCustomer={isUpdatingRentalOrderCustomer}
          customerId={props.rentalOrder?.customerId}
          resourceId={EntityResourceIds.rentalOrder}
          recordId={props.rentalOrder?.rentalOrderId}
          onChange={openChangeMode}
        />
      </Hide>
      <Show when={isChangeModeOpen}>
        <Card
          data-testid={suffixTestId('customerCard', props)}
          title={i18n.t('entity.customer.labels.customer')}
          actions={[
            {
              type: 'button',
              variant: 'dangerLink',
              leftIcon: 'navigation/cancel',
              title: i18n.t('general.actions.discardChanges'),
              'data-testid': testIds.rentalOrder.vehicleAndCustomer('discardChanges-customer'),
              onClick: closeChangeMode,
            },
          ]}
        >
          <CustomerMatchOrCreate
            data-testid={testIds.rentalOrder.detail('customer')}
            onCustomer={handleUpdateCustomer}
          />
        </Card>
      </Show>
    </>
  );
}
